import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import oktaAuth, { extractGroupsFromToken } from '../lib/oktaClient';
import {
  setUser,
  setToken,
  logout,
  DEV_MODE_PLACEHOLDER_TOKEN,
} from '../store/features/auth/authSlice';
import type { User } from '../store/types';
import type { AppDispatch } from '../store';

// c8 ignore next — module-level env var default; both branches untestable without module reload
const API_BASE = (import.meta.env.VITE_API_BASE_URL as string) ?? 'http://localhost:3001';

interface UserProfile {
  roles: string[];
  teams: string[];
}

async function fetchUserProfile(accessToken: string): Promise<UserProfile> {
  try {
    const res = await fetch(`${API_BASE}/api/v1/users/me`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    /* c8 ignore next */
    if (!res.ok) return { roles: extractGroupsFromToken(accessToken), teams: [] };
    const body = (await res.json()) as { data?: { roles?: string[]; teams?: string[] } };
    return {
      roles: body.data?.roles ?? extractGroupsFromToken(accessToken),
      teams: body.data?.teams ?? [],
    };
    /* c8 ignore next */
  } catch {
    /* c8 ignore next */
    return { roles: extractGroupsFromToken(accessToken), teams: [] };
  }
}

const DEV_MODE = import.meta.env.VITE_OKTA_DEV_MODE === 'true';

export interface UseOktaAuthReturn {
  isAuthenticated: boolean;
  isLoading: boolean;
  signOut: () => Promise<void>;
}

function buildDevUser(): User {
  return {
    id: 'dev-user',
    email: 'dev@example.com',
    name: 'Dev User',
    roles: ['Administrator'],
    teams: [],
  };
}

export function useOktaAuth(): UseOktaAuthReturn {
  const dispatch = useDispatch<AppDispatch>();
  const [isAuthenticated, setIsAuthenticated] = useState(DEV_MODE);
  const [isLoading, setIsLoading] = useState(!DEV_MODE);

  useEffect(() => {
    if (DEV_MODE) {
      dispatch(setUser(buildDevUser()));
      dispatch(setToken(DEV_MODE_PLACEHOLDER_TOKEN));
      return;
    }

    async function initAuth(): Promise<void> {
      let redirecting = false;
      try {
        // If Okta is redirecting back to us, the callback route handles it.
        // Drop isLoading immediately — the callback component renders its own
        // "Completing sign-in..." UI and manages all success/failure paths.
        if (oktaAuth.isLoginRedirect()) {
          return;
        }

        const authenticated = await oktaAuth.isAuthenticated();

        if (authenticated) {
          const token = oktaAuth.getAccessToken();
          const userInfo = await oktaAuth.getUser();

          if (token) {
            dispatch(setToken(token));
          }

          const profile = token ? await fetchUserProfile(token) : { roles: [], teams: [] };
          dispatch(
            setUser({
              id: userInfo.sub,
              email: userInfo.email ?? '',
              name: userInfo.name ?? '',
              roles: profile.roles,
              teams: profile.teams,
            })
          );
          setIsAuthenticated(true);
        } else {
          // Keep isLoading=true while redirecting so AppShell never renders
          // children with a missing token before the browser navigates away.
          redirecting = true;
          const orgKey = (import.meta.env.VITE_OKTA_ORG_KEY as string | undefined)?.trim();
          await oktaAuth.signInWithRedirect({
            // okta-auth-js only calls setOriginalUri() when this is explicitly
            // passed (esm/browser/oidc/mixin/index.js) — without it,
            // auth/callback.tsx's getOriginalUri() always returns undefined
            // and every deep link that required a fresh login silently fell
            // back to '/' (issue #1771 follow-up).
            originalUri: window.location.pathname + window.location.search + window.location.hash,
            // c8 ignore next — VITE_OKTA_ORG_KEY is never set in tests; requires module reload
            ...(orgKey ? { extraParams: { organizationKey: orgKey } } : {}),
          });
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
      } finally {
        if (!redirecting) {
          setIsLoading(false);
        }
      }
    }

    initAuth();
  }, [dispatch]);

  const signOut = useCallback(async (): Promise<void> => {
    dispatch(logout());
    if (!DEV_MODE) {
      // Clear storage first — signOut redirect may fail (CORS/no end_session_endpoint)
      // without reaching the internal tokenManager.clear() step
      oktaAuth.tokenManager.clear();
      try {
        // Identity Gateway has no /oauth2/v1/revoke endpoint — skip revocation.
        // postLogoutRedirectUri sends IdP back to root, not /auth/callback.
        await oktaAuth.signOut({
          revokeAccessToken: false,
          revokeRefreshToken: false,
          postLogoutRedirectUri: window.location.origin + '/',
        });
      } catch {
        window.location.assign('/');
      }
    }
  }, [dispatch]);

  return { isAuthenticated, isLoading, signOut };
}
